import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import { paths } from "@shared-core/routing/routes";
import { useCustomerDataAdapter } from "@shared-core/data/CustomerDataAdapterContext";
import { useCustomerBasketAdapter } from "@shared-core/data/CustomerBasketAdapterContext";
import type {
  CustomerBasketArtworkSlot,
  CustomerBasketLine,
  CustomerBasketQuoteRequest,
  CustomerBasketView,
  CustomerMovedLinePrice,
} from "@shared-core/data/customer-basket-types";
import { formatDateOrDash } from "@shared-core/domain/formatters";
import { customerBasketFailure } from "@shared-core/domain/customer-basket-presentation";
import { addBackRequest } from "@shared-core/domain/customer-address-book";
import {
  movedPriceNotices,
  shouldRefreshOnArrival,
} from "@shared-core/domain/customer-basket-refresh";
import { Card } from "../../../app/components/primitives/card";
import { Button } from "../../../app/components/primitives/button";
import { usePortalViewer } from "../PortalViewerContext";
import { NO_BASKET_CAPABILITIES } from "@shared-core/data/customer-basket-adapter";
import { BasketGate } from "./BasketGate";
import { BasketLineCard } from "./BasketLineCard";
import { BasketSummaryLayout } from "./BasketSummary";
import { useCustomerBasket } from "./CustomerBasketProvider";
import { QuoteRequestCard } from "./QuoteRequestCard";
import { CheckCircle2, Info, Loader2, ShoppingBasket, X } from "lucide-react";

// The customer's open basket at the customer they are working as (docs/features/customer-portal-ordering/17
// section 3.2), in the quote page's layout: the lines listed once, and the money, Check out and Discard in one
// summary beside them (a slim bar and sheet on a phone). It reads the one composed view and every change posts
// to the URL the view carries. Adding a file is optional here: the person can add it now or after ordering,
// from the link we email.
//
// Where this build cannot change the basket (the real build until the change routes exist) the page shows the
// basket as read: no quantity box, Remove, file button, Check out or Discard, and one sentence saying so.
//
// A basket held for weeks carries the prices and dates its lines were priced with. When the read flags a line
// (`needs_refresh`) the page asks once on arrival for today's (`refreshBasket`), reads the basket again, and shows a
// dismissible "was £86.00, now £80.00" for each price that moved, rises and falls alike. A line the server could not
// confirm keeps what it had and says "Price to confirm"; Check out stays available and is the gate: it prices every
// line again and refuses a price that moved.
//
// A basket holding a paper we price by hand (a quote request, spec 32 §5.7) lists it after the lines as "Quote on
// request", and offers Send to us for a price in place of Check out: the whole basket becomes one request our team
// prices, and the person sees the quote in Quotes once it is published.

export function PortalBasketPage() {
  // Held above the gate: the basket is read again once it is sent, and the gate shows its loading card meanwhile.
  const [sentQuoteNo, setSentQuoteNo] = useState<string | null>(null);
  if (sentQuoteNo !== null) return <SentForAPrice quoteNo={sentQuoteNo} />;
  return (
    <BasketGate>
      {(basket, refresh) => (
        <BasketBody basket={basket} refresh={refresh} onSent={setSentQuoteNo} />
      )}
    </BasketGate>
  );
}

/** The basket has gone to our team as one request: where the quote will be, and its reference. */
function SentForAPrice({ quoteNo }: { quoteNo: string }) {
  return (
    <div className="space-y-5">
      <h1 className="text-xl font-semibold tracking-tight">Your basket</h1>
      <Card
        role="status"
        className="flex flex-col items-center gap-3 p-10 text-center"
      >
        <CheckCircle2 className="size-8 text-emerald-600" />
        <p className="font-medium">We&apos;ve got your request</p>
        <p className="max-w-sm text-sm text-muted-foreground">
          Our team will price everything in it and send you a quote. You&apos;ll
          find it under Quotes, reference {quoteNo}, where you can accept it.
        </p>
        <Button asChild>
          <Link to={paths.portalCatalogue()}>Back to the catalogue</Link>
        </Button>
      </Card>
    </div>
  );
}

export function BasketBody({
  basket,
  refresh,
  onSent,
}: {
  basket: CustomerBasketView;
  refresh: () => void;
  /** The basket was sent to us for a price: the request's reference. */
  onSent?: (quoteNo: string) => void;
}) {
  const baskets = useCustomerBasketAdapter();
  const basketContext = useCustomerBasket();
  const capabilities = basketContext?.capabilities ?? NO_BASKET_CAPABILITIES;
  const canChange = capabilities.changes;
  const readOnlyNote = basketContext?.readOnlyNote ?? null;
  const { isStaffView } = usePortalViewer();
  const data = useCustomerDataAdapter();
  const navigate = useNavigate();
  const [busyLineId, setBusyLineId] = useState<string | null>(null);
  const [lineErrors, setLineErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  // The one refresh a visit makes, what it moved, and which of those notices the person has dismissed.
  const [refreshState, setRefreshState] = useState<"idle" | "running" | "done">(
    "idle",
  );
  const [movedPrices, setMovedPrices] = useState<CustomerMovedLinePrice[]>([]);
  const [dismissedNotices, setDismissedNotices] = useState<ReadonlySet<string>>(
    new Set(),
  );
  const hasAskedToRefresh = useRef(false);
  // One key per visit to the page, reused for retries, so a double click sends the basket once.
  const [sendKey] = useState(() => crypto.randomUUID());
  const [isSending, setIsSending] = useState(false);
  const quoteRequests = basket.quote_requests ?? [];

  useEffect(() => {
    const url = basket.actions.refresh_url ?? null;
    if (hasAskedToRefresh.current || url === null) return;
    if (!canChange || !shouldRefreshOnArrival(basket)) return;
    hasAskedToRefresh.current = true;
    setRefreshState("running");
    baskets
      .refreshBasket(url)
      .then((result) => {
        setMovedPrices(result.changes);
        refresh();
      })
      // A refresh that fails leaves every line as stored, saying "Price to confirm"; checkout prices them again.
      .catch(() => undefined)
      .finally(() => setRefreshState("done"));
  }, [baskets, basket, canChange, refresh]);

  async function removeQuoteRequest(request: CustomerBasketQuoteRequest) {
    const url = request.actions.remove_url;
    if (url === null) return;
    setBusyLineId(request.quote_request_id);
    setBanner(null);
    try {
      await baskets.removeBasketLine(url);
      refresh();
    } catch (err) {
      setLineErrors((prev) => ({
        ...prev,
        [request.quote_request_id]: customerBasketFailure(err).summary,
      }));
    } finally {
      setBusyLineId(null);
    }
  }

  async function sendForAPrice(url: string) {
    setIsSending(true);
    setBanner(null);
    try {
      const sent = await baskets.sendBasketForAPrice(url, {
        checkout_key: sendKey,
      });
      onSent?.(sent.quote_no);
      refresh();
    } catch (err) {
      setBanner(customerBasketFailure(err).summary);
    } finally {
      setIsSending(false);
    }
  }

  /** Runs a change to one line and reads the basket again; true when it went through. */
  async function changeLine(
    line: CustomerBasketLine,
    change: () => Promise<void>,
  ): Promise<boolean> {
    setBusyLineId(line.line_id);
    setBanner(null);
    setLineErrors((prev) => {
      const next = { ...prev };
      delete next[line.line_id];
      return next;
    });
    try {
      await change();
      refresh();
      return true;
    } catch (err) {
      const failure = customerBasketFailure(err);
      const message = failure.fieldErrors.quantity ?? failure.summary;
      setLineErrors((prev) => ({ ...prev, [line.line_id]: message }));
      return false;
    } finally {
      setBusyLineId(null);
    }
  }

  async function discard(url: string) {
    setBanner(null);
    try {
      await baskets.discardBasket(url);
      refresh();
    } catch (err) {
      setBanner(customerBasketFailure(err).summary);
    }
  }

  // The same file check as the portal's Check artwork page, then the check goes on the slot.
  async function attachFile(slot: CustomerBasketArtworkSlot, file: File) {
    if (slot.attach_url === null) return;
    const check = await data.submitArtworkCheck(file);
    await baskets.attachArtworkCheck(slot.attach_url, slot, check.check_id);
    refresh();
  }

  async function detachFile(slot: CustomerBasketArtworkSlot) {
    if (slot.detach_url === null) return;
    await baskets.detachArtworkCheck(slot.detach_url, slot);
    refresh();
  }

  if (basket.lines.length === 0 && quoteRequests.length === 0) {
    return (
      <div className="space-y-5">
        <PageHeading
          basket={basket}
          readOnlyNote={canChange ? null : readOnlyNote}
          canChangeFiles={capabilities.files}
        />
        <Card className="flex flex-col items-center gap-3 p-10 text-center">
          <ShoppingBasket className="size-8 text-muted-foreground" />
          {/* Where nothing can be changed the server's own sentence says why there is no basket to show: "Your
              basket is empty.", or, for staff looking at a whole customer, that a customer has people's baskets. */}
          <p className="font-medium">
            {!canChange && basket.blocked_reason
              ? basket.blocked_reason
              : "Your basket is empty"}
          </p>
          <p className="max-w-sm text-sm text-muted-foreground">
            {canChange
              ? "Choose something from your catalogue and it will wait here until you're ready to order."
              : readOnlyNote}
          </p>
          {!isStaffView && (
            <Button asChild>
              <Link to={paths.portalCatalogue()}>Browse the catalogue</Link>
            </Button>
          )}
        </Card>
      </div>
    );
  }

  const canCheckOut =
    capabilities.checkout &&
    basket.blocked_reason === null &&
    basket.actions.place_order_url !== null;

  return (
    <BasketSummaryLayout
      header={
        <div className="space-y-5">
          <PageHeading
            basket={basket}
            readOnlyNote={canChange ? null : readOnlyNote}
            canChangeFiles={capabilities.files}
          />
          {banner && (
            <p
              role="alert"
              className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/30 dark:text-red-200"
            >
              {banner}
            </p>
          )}
          <PriceChangeNotices
            notices={movedPriceNotices(
              movedPrices,
              basket.lines,
              dismissedNotices,
            )}
            onDismiss={(lineId) =>
              setDismissedNotices((prev) => new Set(prev).add(lineId))
            }
          />
        </div>
      }
      summary={{
        label: "Your total and checking out",
        sheetTitle: "Your basket total",
        totals: basket.totals,
        // Why Check out waits, in the server's words, once: in the summary, the bar and the sheet alike.
        state:
          (canChange || capabilities.checkout) && basket.blocked_reason
            ? { text: basket.blocked_reason, isOwed: true }
            : null,
        barAction: canCheckOut
          ? {
              label: "Check out ›",
              onPress: () => navigate(paths.portalCheckout()),
            }
          : null,
        children: (
          <SummaryActions
            basket={basket}
            canChange={canChange}
            hasCheckout={capabilities.checkout}
            canCheckOut={canCheckOut}
            onDiscard={discard}
            isSending={isSending}
            onSendForAPrice={sendForAPrice}
          />
        ),
      }}
    >
      <ul className="space-y-4">
        {basket.lines.map((line) => (
          <li key={line.line_id}>
            <BasketLineCard
              line={line}
              canChange={canChange}
              canChangeFiles={capabilities.files}
              busy={busyLineId === line.line_id}
              isCheckingPrice={refreshState === "running"}
              error={lineErrors[line.line_id] ?? null}
              onSetQuantity={(target, quantity) => {
                const url = target.actions.set_quantity_url;
                if (url === null) return;
                void changeLine(target, () =>
                  baskets.setBasketLineQuantity(url, { quantity }),
                );
              }}
              onRemove={(target) => {
                const url = target.actions.remove_url;
                if (url === null) return;
                void changeLine(target, () => baskets.removeBasketLine(url));
              }}
              onAddFile={attachFile}
              onRemoveFile={detachFile}
              onAddBackAddress={(target, removed) => {
                const request = addBackRequest(removed);
                if (removed.add_back_url === null || request === null) return;
                const url = removed.add_back_url;
                void changeLine(target, async () => {
                  await baskets.addAddressToBook(url, request);
                });
              }}
              onDeliveryChanged={refresh}
              onChooseAnotherAddress={(target) => {
                const url = target.actions.remove_url;
                if (url === null) return;
                // The item goes, and its page opens to add it again: a line cannot be moved to another address.
                void changeLine(target, () =>
                  baskets.removeBasketLine(url),
                ).then((isRemoved) => {
                  if (isRemoved)
                    navigate(
                      paths.portalCatalogueItem({ itemId: target.item_id }),
                    );
                });
              }}
            />
          </li>
        ))}
        {quoteRequests.map((request) => (
          <li key={request.quote_request_id}>
            <QuoteRequestCard
              request={request}
              canChange={canChange}
              busy={busyLineId === request.quote_request_id}
              error={lineErrors[request.quote_request_id] ?? null}
              onRemove={(target) => void removeQuoteRequest(target)}
            />
          </li>
        ))}
      </ul>
    </BasketSummaryLayout>
  );
}

/** The prices a refresh moved, each dismissible: what the line was, and what it is now (ex VAT), rises and falls alike. */
function PriceChangeNotices({
  notices,
  onDismiss,
}: {
  notices: ReturnType<typeof movedPriceNotices>;
  onDismiss: (lineId: string) => void;
}) {
  if (notices.length === 0) return null;
  return (
    <ul
      role="status"
      aria-label="Prices that changed"
      className="space-y-2"
      data-testid="price-change-notices"
    >
      {notices.map((notice) => (
        <li
          key={notice.lineId}
          className="flex items-start gap-2 rounded-md border border-sky-300 bg-sky-50 p-3 text-sm text-sky-900 dark:border-sky-800 dark:bg-sky-950/30 dark:text-sky-200"
        >
          <Info className="mt-0.5 size-4 shrink-0" />
          <p className="min-w-0 flex-1">
            <span className="font-medium">{notice.title}:</span> was{" "}
            <span className="tabular-nums">{notice.was}</span>, now{" "}
            <span className="font-medium tabular-nums">{notice.now}</span> (ex
            VAT)
          </p>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-6 shrink-0"
            aria-label={`Dismiss the notice about ${notice.title}`}
            onClick={() => onDismiss(notice.lineId)}
          >
            <X />
          </Button>
        </li>
      ))}
    </ul>
  );
}

/**
 * Under the totals, in the panel and in the phone's sheet: how long we hold the basket, Continue shopping and
 * Discard side by side (equal width, wrapping when the panel is narrow), then Check out alone, full width, last.
 */
function SummaryActions({
  basket,
  canChange,
  hasCheckout,
  canCheckOut,
  onDiscard,
  isSending,
  onSendForAPrice,
}: {
  basket: CustomerBasketView;
  /** Discard is a basket change. */
  canChange: boolean;
  /** Check out is its own group of routes. */
  hasCheckout: boolean;
  canCheckOut: boolean;
  onDiscard: (url: string) => Promise<void>;
  isSending: boolean;
  /** Send to us for a price, for a basket holding a paper we price by hand. */
  onSendForAPrice: (url: string) => Promise<void>;
}) {
  const { isStaffView } = usePortalViewer();
  const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);
  const discardUrl = basket.actions.discard_url;
  const canDiscard = canChange && discardUrl !== null;
  const sendUrl = basket.actions.send_for_a_price_url ?? null;
  return (
    <div className="space-y-4">
      {basket.expires_on && (
        <p className="text-sm text-muted-foreground">
          We&apos;ll hold your basket until{" "}
          {formatDateOrDash(basket.expires_on)}.
        </p>
      )}
      {canDiscard && isConfirmingDiscard ? (
        <div className="space-y-2 rounded-md border p-3 text-sm">
          <p>Discard your whole basket? Everything in it will be removed.</p>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="destructive"
              size="sm"
              onClick={() => void onDiscard(discardUrl)}
            >
              Yes, discard it
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsConfirmingDiscard(false)}
            >
              Keep it
            </Button>
          </div>
        </div>
      ) : (
        (!isStaffView || canDiscard) && (
          <div className="flex flex-wrap gap-3">
            {!isStaffView && (
              <Button
                variant="outline"
                asChild
                className="min-w-36 flex-1 basis-36"
              >
                <Link to={paths.portalCatalogue()}>Continue shopping</Link>
              </Button>
            )}
            {canDiscard && (
              <Button
                variant="outline"
                className="min-w-36 flex-1 basis-36"
                onClick={() => setIsConfirmingDiscard(true)}
              >
                Discard basket
              </Button>
            )}
          </div>
        )
      )}
      {hasCheckout && sendUrl !== null && (
        <Button
          size="lg"
          className="w-full"
          disabled={isSending}
          onClick={() => void onSendForAPrice(sendUrl)}
        >
          {isSending && <Loader2 className="animate-spin" />}
          Send to us for a price
        </Button>
      )}
      {hasCheckout && sendUrl === null && (
        <Button
          size="lg"
          className="w-full"
          asChild={canCheckOut}
          disabled={!canCheckOut}
        >
          {canCheckOut ? (
            <Link to={paths.portalCheckout()}>Check out</Link>
          ) : (
            "Check out"
          )}
        </Button>
      )}
    </div>
  );
}

function PageHeading({
  basket,
  readOnlyNote,
  canChangeFiles,
}: {
  basket: CustomerBasketView;
  /** Where basket changes are off: why. Null where they are on. */
  readOnlyNote: string | null;
  canChangeFiles: boolean;
}) {
  return (
    <div>
      <h1 className="text-xl font-semibold tracking-tight">Your basket</h1>
      <p className="text-sm text-muted-foreground">
        For {basket.customer_name}.
      </p>
      {basket.lines.length > 0 && readOnlyNote !== null && (
        <p role="note" className="text-sm text-muted-foreground">
          {readOnlyNote}
        </p>
      )}
      {basket.lines.length > 0 && readOnlyNote === null && canChangeFiles && (
        <p className="text-sm text-muted-foreground">
          You can add your files now, or after you order from the link we email
          you.
        </p>
      )}
    </div>
  );
}
